// ============================================
// GLOBAL ERROR HANDLER
// ============================================
// Merkezi hata yönetimi - tüm API hatalarını yakalar
// ============================================

import toast from 'react-hot-toast';

/**
 * API hatasından kullanıcı dostu mesaj çıkarır
 */
export function getErrorMessage(error: any, defaultMessage: string = 'Bir hata oluştu'): string {
  const status = error?.response?.status || error?.status;
  const serverMessage = error?.response?.data?.message || error?.data?.message || error?.message;

  // Status code bazlı mesajlar
  const statusMessages: Record<number, string> = {
    400: 'Geçersiz istek. Lütfen bilgileri kontrol edin.',
    401: 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.',
    402: 'Bu işlem için krediniz yetersiz.',
    403: 'Bu işlem için yetkiniz bulunmuyor.',
    404: 'Aradığınız kaynak bulunamadı.',
    409: 'Bu işlem zaten gerçekleştirilmiş.',
    413: 'Dosya boyutu çok büyük.',
    422: 'Form verileri hatalı. Lütfen kontrol edin.',
    429: 'Çok fazla istek gönderdiniz. Lütfen bekleyin.',
    500: 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.',
    502: 'Sunucuya ulaşılamıyor.',
    503: 'Servis geçici olarak kullanılamıyor.',
  };

  // Bilinen sunucu mesajları - Türkçe karşılıkları
  const knownMessages: Record<string, string> = {
    // Auth
    'Invalid credentials': 'Geçersiz e-posta veya şifre.',
    'Invalid email or password': 'Geçersiz e-posta veya şifre.',
    'Email already exists': 'Bu e-posta adresi zaten kayıtlı.',
    'User not found': 'Kullanıcı bulunamadı.',
    'Account disabled': 'Hesabınız devre dışı bırakılmış.',
    'Invalid token': 'Geçersiz veya süresi dolmuş bağlantı.',
    'Token expired': 'Bağlantı süresi dolmuş.',
    'Unauthorized': 'Oturumunuz sona erdi.',
    'Forbidden': 'Bu işlem için yetkiniz yok.',
    
    // Jobs
    'Job not found': 'İş ilanı bulunamadı.',
    'Job expired': 'İş ilanının süresi dolmuş.',
    'Job closed': 'Bu ilan başvuruya kapatılmış.',
    'Insufficient credits': 'Krediniz yetersiz.',
    
    // Applications
    'Already applied': 'Bu ilana zaten başvurdunuz.',
    'Application not found': 'Başvuru bulunamadı.',
    
    // Company
    'Company not found': 'Şirket bulunamadı.',
    
    // Generic
    'Not found': 'Kaynak bulunamadı.',
    'Internal server error': 'Sunucu hatası oluştu.',
    'Network error': 'Bağlantı hatası. İnternet bağlantınızı kontrol edin.',
  };

  // Önce sunucu mesajını kontrol et
  if (serverMessage) {
    // Array veya object gelebilir (NestJS validation) — güvenli string'e çevir
    const msgStr = Array.isArray(serverMessage)
      ? serverMessage.join(', ')
      : typeof serverMessage === 'string'
        ? serverMessage
        : typeof serverMessage === 'object'
          ? JSON.stringify(serverMessage)
          : String(serverMessage);

    // Birebir eşleşme
    if (knownMessages[msgStr]) {
      return knownMessages[msgStr];
    }

    // Kısmi eşleşme
    const lowerMessage = msgStr.toLowerCase();
    for (const [key, value] of Object.entries(knownMessages)) {
      if (lowerMessage.includes(key.toLowerCase())) {
        return value;
      }
    }

    // Sunucu mesajı varsa ve makul uzunluktaysa göster
    if (msgStr.length > 0 && msgStr.length < 200) {
      return msgStr;
    }
  }

  // Status code mesajı
  if (status && statusMessages[status]) {
    return statusMessages[status];
  }

  return defaultMessage;
}

/**
 * Global mutation error handler
 * QueryClient defaultOptions'ta kullanılır
 */
export function createMutationErrorHandler(
  logout: () => void,
  clearQueryCache: () => void
) {
  return (error: any) => {
    const status = error?.response?.status || error?.status;

    // mutationFn içinden doğrudan fırlatılan Error'lar (response yok, _handled flag var)
    // bunlar kendi per-call onError'larında işleniyor, global toast gösterme
    if (!status && !error?.response && error instanceof Error && !(error as any)._globalHandle) {
      return;
    }

    // 401 - Unauthorized: Oturumu sonlandır ve login'e yönlendir
    if (status === 401) {
      toast.error('Oturumunuz sona erdi. Lütfen tekrar giriş yapın.');
      logout();
      clearQueryCache();
      window.location.href = '/login';
      return;
    }

    // 403 - Forbidden: Yetki hatası
    if (status === 403) {
      toast.error('Bu işlem için yetkiniz bulunmuyor.');
      return;
    }

    // 402 - Payment Required: Kredi yetersiz
    if (status === 402) {
      toast.error('Bu işlem için krediniz yetersiz. Lütfen kredi satın alın.');
      return;
    }

    // 429 - Too Many Requests: Rate limit
    if (status === 429) {
      toast.error('Çok fazla istek gönderdiniz. Lütfen biraz bekleyin.');
      return;
    }

    // Network error
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
      toast.error('Bağlantı hatası. İnternet bağlantınızı kontrol edin.');
      return;
    }

    // Diğer hatalar için genel mesaj
    const message = getErrorMessage(error);
    toast.error(message);
  };
}

/**
 * Global query error handler (opsiyonel - sadece kritik hatalar için)
 */
export function createQueryErrorHandler(
  logout: () => void,
  clearQueryCache: () => void
) {
  return (error: any) => {
    const status = error?.response?.status || error?.status;

    // Sadece 401 için logout yap ve login'e yönlendir
    // (Query hataları genelde UI'da gösterilir)
    if (status === 401) {
      logout();
      clearQueryCache();
      window.location.href = '/login';
    }
  };
}
